import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Get site URL dynamically - use production URL if available, otherwise use current origin
const getSiteUrl = () => {
  if (typeof window !== 'undefined') {
    // For production, use the actual domain
    const hostname = window.location.hostname;
    if (hostname === '13cabsbook.com.au' || hostname.includes('13cabsbook')) {
      return 'https://13cabsbook.com.au';
    }
    return window.location.origin;
  }
  return 'https://13cabsbook.com.au';
};

// Site-wide SEO configuration
const SITE_CONFIG = {
  siteName: '13cabsbook',
  siteUrl: getSiteUrl(),
  phone: '1300 096 685',
  email: '13cabsbook.com.au@gmail.com',
  logo: 'https://customer-assets.emergentagent.com/job_quick-cab-booking/artifacts/zydgwvt1_13cabsbook%20logo%20short.png',
  address: {
    city: 'Melbourne',
    state: 'Victoria',
    country: 'Australia',
    postalCode: '3000'
  }
};

// Per-page SEO data
const PAGE_SEO = {
  '/': {
    title: '13cabsbook - Melbourne Taxi Booking | 24/7 Cab Service Victoria',
    description: 'Book a taxi in Melbourne with 13cabsbook. Professional drivers, competitive rates, 24/7 service. Airport transfers, corporate travel, maxi taxis. Call 1300 096 685.',
    keywords: 'taxi melbourne, cab booking melbourne, melbourne taxi service, airport taxi melbourne, 24/7 taxi, maxi taxi melbourne, corporate taxi, 13cabsbook',
    ogType: 'website'
  },
  '/fleet': {
    title: 'Our Fleet - Sedan, SUV, Premium & Maxi Taxis | 13cabsbook Melbourne',
    description: 'Choose from our fleet of sedans, premium Mercedes, SUVs, Lexus, and maxi taxis. Comfortable, clean vehicles for 1-11 passengers. Book your perfect ride today.',
    keywords: 'taxi fleet melbourne, sedan taxi, premium taxi, suv taxi, maxi taxi, lexus taxi, mercedes taxi, 13cabsbook fleet',
    ogType: 'website'
  },
  '/airport-transfers': {
    title: 'Melbourne Airport Transfers | Tullamarine & Avalon Taxi | 13cabsbook',
    description: 'Reliable Melbourne Airport taxi transfers. Fixed prices, flight tracking, meet & greet service. Tullamarine (MEL) and Avalon (AVV) airport pickups. Book now!',
    keywords: 'melbourne airport taxi, tullamarine taxi, avalon airport transfer, airport pickup melbourne, mel airport taxi, flight tracking taxi',
    ogType: 'website'
  },
  '/corporate': {
    title: 'Corporate Taxi Services Melbourne | Business Accounts | 13cabsbook',
    description: 'Corporate taxi solutions for Melbourne businesses. Executive transfers, staff transport, monthly billing, dedicated account management. Open a corporate account today.',
    keywords: 'corporate taxi melbourne, business taxi account, executive transfers, staff transport, corporate travel melbourne',
    ogType: 'website'
  },
  '/events': {
    title: 'Events & Sports Taxi Melbourne | MCG, Rod Laver, Concerts | 13cabsbook',
    description: 'Taxi service for Melbourne events, sports, and concerts. MCG, Rod Laver Arena, Marvel Stadium transfers. Skip the parking hassle - book your event taxi now.',
    keywords: 'event taxi melbourne, mcg taxi, rod laver arena taxi, concert transport melbourne, sports event taxi, melbourne cup taxi',
    ogType: 'website'
  },
  '/contact': {
    title: 'Contact 13cabsbook | Melbourne Taxi Company | 1300 096 685',
    description: 'Contact 13cabsbook for taxi bookings and inquiries. Call 1300 096 685 or email us. 24/7 service across Melbourne and Victoria. We\'re here to help!',
    keywords: 'contact 13cabsbook, melbourne taxi phone, taxi booking contact, 1300 096 685',
    ogType: 'website'
  }
};

// Generate LocalBusiness Schema
const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_CONFIG.siteUrl}/#business`,
  "name": "13cabsbook",
  "alternateName": "13cabsbook Melbourne Taxi",
  "description": "Professional taxi and cab booking service in Melbourne, Victoria. 24/7 airport transfers, corporate travel, event transport, and maxi taxi services.",
  "url": SITE_CONFIG.siteUrl,
  "logo": SITE_CONFIG.logo,
  "image": SITE_CONFIG.logo,
  "telephone": SITE_CONFIG.phone,
  "email": SITE_CONFIG.email,
  "address": {
    "@type": "PostalAddress",
    "addressLocality": SITE_CONFIG.address.city,
    "addressRegion": SITE_CONFIG.address.state,
    "addressCountry": SITE_CONFIG.address.country,
    "postalCode": SITE_CONFIG.address.postalCode
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "-37.8136",
    "longitude": "144.9631"
  },
  "areaServed": [
    {
      "@type": "State",
      "name": "Victoria",
      "containedInPlace": {
        "@type": "Country",
        "name": "Australia"
      }
    },
    {
      "@type": "City",
      "name": "Melbourne"
    }
  ],
  "priceRange": "$$",
  "currenciesAccepted": "AUD",
  "paymentAccepted": "Cash, Credit Card, EFTPOS, Cabcharge",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "00:00",
    "closes": "23:59"
  },
  "sameAs": [],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "500",
    "bestRating": "5",
    "worstRating": "1"
  }
});

// Generate TaxiService Schema
const generateTaxiServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "TaxiService",
  "@id": `${SITE_CONFIG.siteUrl}/#taxiservice`,
  "name": "13cabsbook Taxi Service",
  "description": "Melbourne's trusted taxi service offering sedan, SUV, premium, and maxi taxi options for airport transfers, corporate travel, events, and daily commutes.",
  "provider": {
    "@type": "LocalBusiness",
    "@id": `${SITE_CONFIG.siteUrl}/#business`
  },
  "areaServed": {
    "@type": "State",
    "name": "Victoria",
    "containedInPlace": {
      "@type": "Country",
      "name": "Australia"
    }
  },
  "serviceType": [
    "Airport Transfer",
    "Corporate Taxi",
    "Event Transport",
    "Maxi Taxi",
    "Premium Sedan",
    "SUV Taxi"
  ],
  "offers": {
    "@type": "Offer",
    "availability": "https://schema.org/InStock",
    "priceCurrency": "AUD",
    "priceSpecification": {
      "@type": "PriceSpecification",
      "priceCurrency": "AUD"
    }
  },
  "termsOfService": `${SITE_CONFIG.siteUrl}/contact`
});

// Generate Organization Schema
const generateOrganizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_CONFIG.siteUrl}/#organization`,
  "name": "13cabsbook",
  "legalName": "13cabsbook Melbourne Taxi",
  "url": SITE_CONFIG.siteUrl,
  "logo": SITE_CONFIG.logo,
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": SITE_CONFIG.phone,
    "contactType": "customer service",
    "email": SITE_CONFIG.email,
    "availableLanguage": ["English"],
    "areaServed": "AU",
    "hoursAvailable": {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      "opens": "00:00",
      "closes": "23:59"
    }
  }
});

// Generate WebSite Schema with SearchAction
const generateWebSiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_CONFIG.siteUrl}/#website`,
  "name": "13cabsbook",
  "url": SITE_CONFIG.siteUrl,
  "description": "Book a taxi in Melbourne with 13cabsbook. Professional taxi service across Victoria.",
  "publisher": {
    "@type": "Organization",
    "@id": `${SITE_CONFIG.siteUrl}/#organization`
  },
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${SITE_CONFIG.siteUrl}/?search={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
});

// Generate Service offerings schema
const generateServiceSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  "@id": `${SITE_CONFIG.siteUrl}/#services`,
  "name": "13cabsbook Taxi Services",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "item": {
        "@type": "Service",
        "name": "Sedan Taxi",
        "description": "Comfortable standard taxi for everyday travel around Melbourne. Perfect for 1-4 passengers.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne, Victoria"
      }
    },
    {
      "@type": "ListItem",
      "position": 2,
      "item": {
        "@type": "Service",
        "name": "Premium Sedan",
        "description": "Luxury Mercedes sedan for business executives and special occasions. Executive class service.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne, Victoria"
      }
    },
    {
      "@type": "ListItem",
      "position": 3,
      "item": {
        "@type": "Service",
        "name": "SUV Taxi",
        "description": "Spacious SUV taxi with extra legroom and luggage space. Ideal for families and groups up to 6.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne, Victoria"
      }
    },
    {
      "@type": "ListItem",
      "position": 4,
      "item": {
        "@type": "Service",
        "name": "Premium SUV",
        "description": "Luxury Lexus SUV for premium group travel. The ultimate in space and comfort.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne, Victoria"
      }
    },
    {
      "@type": "ListItem",
      "position": 5,
      "item": {
        "@type": "Service",
        "name": "Maxi Taxi",
        "description": "Large capacity taxi for groups up to 11 passengers. Perfect for airport transfers and events.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne, Victoria"
      }
    },
    {
      "@type": "ListItem",
      "position": 6,
      "item": {
        "@type": "Service",
        "name": "Airport Transfers",
        "description": "Reliable Melbourne Airport and Avalon Airport taxi transfers with flight tracking.",
        "provider": { "@id": `${SITE_CONFIG.siteUrl}/#business` },
        "areaServed": "Melbourne Airport, Avalon Airport"
      }
    }
  ]
});

// Generate FAQ Schema
const generateFAQSchema = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SITE_CONFIG.siteUrl}/contact#faq`,
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How do I book a taxi with 13cabsbook?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "You can book online through our website at 13cabsbook.com.au, call us at 1300 096 685, or email your booking details. We recommend booking in advance for airport transfers."
      }
    },
    {
      "@type": "Question",
      "name": "What areas does 13cabsbook service?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We provide taxi services across Melbourne and throughout Victoria, including all suburbs, Melbourne Airport, Avalon Airport, and regional areas like Geelong, Ballarat, and Bendigo."
      }
    },
    {
      "@type": "Question",
      "name": "What types of taxis do you have?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We offer Sedan Taxis (1-4 passengers), Premium Sedans (Mercedes), SUV Taxis (1-6 passengers), Premium SUVs (Lexus), and Maxi Taxis (up to 11 passengers)."
      }
    },
    {
      "@type": "Question",
      "name": "Is 13cabsbook available 24/7?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, 13cabsbook operates 24 hours a day, 7 days a week, including public holidays. You can book a taxi anytime."
      }
    },
    {
      "@type": "Question",
      "name": "What payment methods do you accept?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "We accept cash, EFTPOS, all major credit cards, Cabcharge, and digital wallets. Payment is made directly to the driver at the end of your journey."
      }
    }
  ]
});

// Generate BreadcrumbList Schema
const generateBreadcrumbSchema = (pathname) => {
  const breadcrumbs = [
    { name: 'Home', url: SITE_CONFIG.siteUrl }
  ];

  const pathMap = {
    '/fleet': { name: 'Our Fleet', url: `${SITE_CONFIG.siteUrl}/fleet` },
    '/airport-transfers': { name: 'Airport Transfers', url: `${SITE_CONFIG.siteUrl}/airport-transfers` },
    '/corporate': { name: 'Corporate Services', url: `${SITE_CONFIG.siteUrl}/corporate` },
    '/events': { name: 'Events', url: `${SITE_CONFIG.siteUrl}/events` },
    '/contact': { name: 'Contact', url: `${SITE_CONFIG.siteUrl}/contact` }
  };

  if (pathMap[pathname]) {
    breadcrumbs.push(pathMap[pathname]);
  }

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": crumb.url
    }))
  };
};

// Main SEO Component
export const SEOHead = () => {
  const location = useLocation();
  const pathname = location.pathname;
  const pageSeo = PAGE_SEO[pathname] || PAGE_SEO['/'];

  useEffect(() => {
    // Update document title
    document.title = pageSeo.title;

    // Helper to update or create meta tag
    const setMetaTag = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', pageSeo.description);
    setMetaTag('keywords', pageSeo.keywords);
    setMetaTag('author', '13cabsbook');
    setMetaTag('robots', 'index, follow');

    // Open Graph tags
    setMetaTag('og:title', pageSeo.title, true);
    setMetaTag('og:description', pageSeo.description, true);
    setMetaTag('og:type', pageSeo.ogType, true);
    setMetaTag('og:url', `${SITE_CONFIG.siteUrl}${pathname}`, true);
    setMetaTag('og:site_name', SITE_CONFIG.siteName, true);
    setMetaTag('og:image', SITE_CONFIG.logo, true);
    setMetaTag('og:locale', 'en_AU', true);

    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', pageSeo.title);
    setMetaTag('twitter:description', pageSeo.description);
    setMetaTag('twitter:image', SITE_CONFIG.logo);

    // Additional SEO tags
    setMetaTag('geo.region', 'AU-VIC');
    setMetaTag('geo.placename', 'Melbourne');
    setMetaTag('geo.position', '-37.8136;144.9631');
    setMetaTag('ICBM', '-37.8136, 144.9631');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE_CONFIG.siteUrl}${pathname}`);

    // Remove existing schema scripts
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => script.remove());

    // Add JSON-LD Schema Markup
    const schemas = [
      generateLocalBusinessSchema(),
      generateOrganizationSchema(),
      generateWebSiteSchema(),
      generateTaxiServiceSchema(),
      generateServiceSchema(),
      generateBreadcrumbSchema(pathname)
    ];

    // Add FAQ schema only on contact page
    if (pathname === '/contact') {
      schemas.push(generateFAQSchema());
    }

    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schema);
      document.head.appendChild(script);
    });

  }, [pathname, pageSeo]);

  return null;
};

export default SEOHead;
